import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyManagement = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    address: ''
  });
  const { user, logout, token } = useAuth();

  const isAdminMaster = user?.role?.toLowerCase() === 'admin_master';

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data || []);
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Error al cargar propiedades");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && isAdminMaster) {
      fetchProperties();
    }
  }, [token, isAdminMaster, fetchProperties]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast.error("Nombre y codigo son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/properties`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Propiedad creada exitosamente");
      setShowCreateModal(false);
      setFormData({ name: '', code: '', description: '', address: '' });
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear propiedad");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedProperty) return;
    try {
      await axios.put(`${API}/properties/${selectedProperty.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Propiedad actualizada");
      setShowEditModal(false);
      setSelectedProperty(null);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleDelete = async (propertyId, propertyName) => {
    if (!window.confirm(`¿Desactivar la propiedad "${propertyName}"?`)) return;
    try {
      await axios.delete(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Propiedad desactivada");
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desactivar");
    }
  };

  const openEditModal = (property) => {
    setSelectedProperty(property);
    setFormData({
      name: property.name,
      code: property.code,
      description: property.description || '',
      address: property.address || ''
    });
    setShowEditModal(true);
  };

  if (!isAdminMaster) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Acceso Denegado</h2>
        <p>Solo el Admin Master puede gestionar propiedades.</p>
        <Link to="/admin">Volver al Dashboard</Link>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '20px', 
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, color: '#1f2937' }}>Gestion de Propiedades</h1>
            <p style={{ color: '#6b7280', margin: '5px 0 0' }}>Admin Master: {user?.username}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                setFormData({ name: '', code: '', description: '', address: '' });
                setShowCreateModal(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
              data-testid="add-property-btn"
            >
              + Nueva Propiedad
            </button>
            <Link 
              to="/admin" 
              style={{
                background: '#6b7280',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Volver
            </Link>
            <button 
              onClick={logout}
              style={{
                background: '#ef4444',
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Cerrar Sesion
            </button>
          </div>
        </div>

        {/* Properties Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
          gap: '20px' 
        }}>
          {loading ? (
            <p style={{ color: 'white', gridColumn: '1/-1', textAlign: 'center' }}>Cargando...</p>
          ) : properties.length === 0 ? (
            <p style={{ color: 'white', gridColumn: '1/-1', textAlign: 'center' }}>No hay propiedades registradas</p>
          ) : (
            properties.map(property => (
              <div 
                key={property.id}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  opacity: property.is_active ? 1 : 0.6
                }}
                data-testid={`property-card-${property.code}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#1f2937' }}>{property.name}</h3>
                    <span style={{
                      display: 'inline-block',
                      background: property.is_active ? '#10b981' : '#ef4444',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginTop: '5px'
                    }}>
                      {property.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  <span style={{
                    background: '#667eea',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {property.code}
                  </span>
                </div>
                
                {property.description && (
                  <p style={{ color: '#6b7280', marginTop: '10px', fontSize: '14px' }}>
                    {property.description}
                  </p>
                )}
                
                {property.address && (
                  <p style={{ color: '#9ca3af', marginTop: '5px', fontSize: '13px' }}>
                    📍 {property.address}
                  </p>
                )}
                
                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => openEditModal(property)}
                    style={{
                      flex: 1,
                      background: '#3b82f6',
                      color: 'white',
                      padding: '8px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Editar
                  </button>
                  {property.is_active && (
                    <button
                      onClick={() => handleDelete(property.id, property.name)}
                      style={{
                        flex: 1,
                        background: '#ef4444',
                        color: 'white',
                        padding: '8px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Desactivar
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent style={{ maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle>Nueva Propiedad</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Nombre *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ej: Villa del Palmar Vallarta"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                data-testid="property-name-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Codigo *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                placeholder="Ej: VDP-VALLARTA"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                data-testid="property-code-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Descripcion</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descripcion de la propiedad"
                rows={3}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', resize: 'vertical' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Direccion</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Ej: Puerto Vallarta, Jalisco"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                data-testid="create-property-submit"
              >
                Crear Propiedad
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent style={{ maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle>Editar Propiedad</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Nombre *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Codigo *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Descripcion</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', resize: 'vertical' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Direccion</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Guardar Cambios
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyManagement;
